import mapValues from 'lodash/mapValues';

import {
  MinimalProjectMetadata as MinimalProjectApiResponse,
  ProjectMetadata as ProjectApiResponse,
  ProjectMetadataMap,
} from 'app_api/projects/types';
import {currentProjectClient, projectDetailsClient} from 'common/report/queries/app-api/project-details-client';
import {extendWorkspace, toWorkspaceObj, WorkspaceObj} from 'common/report/queries/app-api/workspaces-client';
import {getPrefixedUrlOptions} from 'common/util/url';
import {getUtcOffset} from 'common/util/date';
import {isTestEnv} from 'common/report/util/env';

import {FeatureGatesValue, ProjectFeatureFlag, ProjectType} from '../constants';
import {Store} from './store';

export interface ExternalHelpLink {
  linkName: string;
  linkURL: string;
}

interface Role {
  id?: number;
  name?: string;
}

export class ProjectDetails {
  public domain: string;
  public eventsPlanID: number;
  public externalHelpLinks: ExternalHelpLink[];
  public hasIntegratedArb: boolean;
  public hasIntegratedEngage: boolean;
  public hasSensitiveAccess: boolean;
  public hasWorkspaces: boolean;
  public id: number;
  public isDemo: boolean;
  public name: string;
  public onboardingDashboardID: number;
  public organizationId: number;
  public ownerID: number;
  public peoplePlanID: number;
  public projectAsOfDate: null | string;
  public role: Role;
  public timezone: string;
  public type: ProjectType;
  public utcOffset: number;
  public workspaces: {[key: string]: WorkspaceObj};

  // these should be accessed through helpers hasFeatureFlag() etc
  private featureFlags: Set<string>;
  private permissions: Set<string>;
  private featureGates: FeatureGatesValue;

  static MINIMAL_COPYABLE_PROPS: {[key: string]: keyof MinimalProjectApiResponse} = {
    domain: `domain`,
    hasWorkspaces: `has_workspaces`,
    id: `id`,
    isDemo: `is_demo`,
    name: `name`,
    organizationId: `organization_id`,
    type: `type`,
  };
  static OTHER_COPYABLE_PROPS: {[key: string]: keyof ProjectApiResponse} = {
    eventsPlanID: `events_plan_id`,
    hasIntegratedArb: `has_integrated_arb`,
    hasIntegratedEngage: `has_integrated_engage`,
    hasSensitiveAccess: `hasSensitiveAccess`,
    onboardingDashboardID: `onboarding_dashboard_id`,
    ownerID: `owner_id`,
    peoplePlanID: `people_plan_id`,
    timezone: `timezone`,
    utcOffset: `utc_offset`,
  };
  static ALL_COPYABLE_PROPS: {[key: string]: keyof ProjectApiResponse} = {
    ...ProjectDetails.MINIMAL_COPYABLE_PROPS,
    ...ProjectDetails.OTHER_COPYABLE_PROPS,
  };

  constructor(projectAttrs: MinimalProjectApiResponse, existingProject?: ProjectDetails);
  constructor(projectAttrs: ProjectApiResponse, existingProject?: ProjectDetails) {
    if (existingProject) {
      for (const prop of Object.keys(ProjectDetails.ALL_COPYABLE_PROPS)) {
        this[prop] = existingProject[prop];
      }
      this.externalHelpLinks = [...existingProject.externalHelpLinks];
      this.featureFlags = new Set(existingProject.featureFlags);
      this.projectAsOfDate = existingProject.projectAsOfDate;
      this.permissions = new Set(existingProject.permissions);
      this.role = {...existingProject.role};
      this.workspaces = {...existingProject.workspaces};
      this.featureGates = existingProject.featureGates;
    } else {
      // defaults
      this.externalHelpLinks = [];
      this.featureFlags = new Set();
      // the app API uses null as the default value to indicate the lack of a project as-of date, but note that
      // you might want to coalesce this to an empty string before passing into attributes because panel attributes cast null to "null"
      this.projectAsOfDate = null;
      this.permissions = new Set();
      this.role = {};
      this.workspaces = {};
      this.featureGates = null;
    }

    // test for minimal vs full project details
    if (projectAttrs.external_help_link) {
      for (const [prop, apiName] of Object.entries(ProjectDetails.ALL_COPYABLE_PROPS)) {
        this[prop] = projectAttrs[apiName];
      }
      this.externalHelpLinks = projectAttrs.external_help_link.map((link) => ({
        linkName: link.link_name,
        linkURL: link.link_url,
      }));
      this.featureFlags = new Set(projectAttrs.feature_flags);
      this.projectAsOfDate = projectAttrs.project_as_of_date;
      this.permissions = new Set(projectAttrs.permissions);
      this.featureGates = projectAttrs.feature_gates as FeatureGatesValue;
      this.role = projectAttrs.role || {};
      this.workspaces = mapValues(projectAttrs.workspaces, toWorkspaceObj);

      // GOOT: webapp server is providing incorrect/oudated UTC offset values, so as a quick fix we use an up-to-date moment-timezone to determine UTC offset
      this.utcOffset = isTestEnv() ? projectAttrs.utc_offset : getUtcOffset(this.timezone);
      if (this.utcOffset !== projectAttrs.utc_offset) {
        console.warn(
          `Discrepancy between server-provided UTC offset (${projectAttrs.utc_offset}) and frontend (${this.utcOffset})!`,
        );
      }
    } else {
      for (const [prop, apiName] of Object.entries(ProjectDetails.MINIMAL_COPYABLE_PROPS)) {
        this[prop] = projectAttrs[apiName];
      }
      this.workspaces = mapValues(projectAttrs.workspaces, (minimalWorkspace) =>
        extendWorkspace(this.workspaces[minimalWorkspace.id] || {}, minimalWorkspace),
      );
    }
  }

  public hasFeatureFlag(flagName: ProjectFeatureFlag) {
    if (this.featureFlags.has(flagName) || this.featureFlags.has(`dev`)) {
      return true;
    } else {
      // support URL query params like ?feature-foobar=1
      const urlFeatures = getPrefixedUrlOptions(`feature`);
      return urlFeatures.has(flagName) || urlFeatures.has(`dev`);
    }
  }

  public getFeatureGate<K extends keyof FeatureGatesValue>(gate: K): FeatureGatesValue[K] | null {
    return this.featureGates?.[gate];
  }

  public hasPermission(permissionName: string) {
    return this.permissions.has(permissionName);
  }

  public updateWorkspaces(workspaces: {[key: string]: WorkspaceObj}) {
    Object.assign(this.workspaces, workspaces);
  }

  public isRollupProject(): boolean {
    return this.type === ProjectType.Rollup;
  }
}

export interface ProjectDetailsMap {
  [projectId: number]: ProjectDetails;
}

export const projectDetailsStore = new Store<ProjectDetailsMap>({
  apiClient: projectDetailsClient,
  defaultValue: {},
  name: `project-details`,
  transform: {
    // yeah, this is grody, a single "transform" function for both INDEX and GET
    // revised Store/Resource API will require API client to do these translations
    index: (projects: ProjectMetadataMap | ProjectApiResponse, {store}): ProjectDetailsMap =>
      `name` in projects
        ? {
            [Number(projects[`id`])]: new ProjectDetails(
              projects as ProjectApiResponse,
              store.value[Number(projects[`id`])],
            ),
          }
        : mapValues(projects, (project) => new ProjectDetails(project, store.value[project.id])),
  },
});

export const currentProjectStore = new Store<ProjectDetails>({
  apiClient: currentProjectClient,
  defaultValue: null,
  name: `current-project`,
  transform: {
    index: (project: ProjectApiResponse) => new ProjectDetails(project),
  },
});
